/**
 * Hook for managing conversational interactions with the Adya orchestrator
 * Handles conversation flow, mode detection, and real-time updates
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { 
  conversationalApi, 
  ConversationResponse, 
  ConversationMessage, 
  WebSocketMessage,
  ConversationalApiError 
} from '../services/conversationalApi'

export interface Message {
  id: string
  type: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  mode?: string
  metadata?: {
    agentName?: string
    stage?: string
    status?: 'thinking' | 'processing' | 'complete'
    workflow_result?: any
  }
}

export interface ConversationState {
  messages: Message[]
  currentMode: string
  isLoading: boolean
  isConnected: boolean
  workflowProgress: {
    currentStage?: string
    status?: string
    stagesCompleted?: number
    totalStages?: number
    agentName?: string
    message?: string
    startedAt?: number
  } | null
  error: string | null
  nextActions: string[]
  memoryContext?: any
}

export interface ConversationActions {
  sendMessage: (content: string) => Promise<ConversationResponse | undefined>
  uploadFile: (files: File | File[]) => Promise<void>
  clearError: () => void
  reconnectWebSocket: () => void
  getProposal: () => Promise<string | null>
  clearConversation: () => void
}

export function useConversation(projectId: string): ConversationState & ConversationActions {
  const [messages, setMessages] = useState<Message[]>([])
  const [currentMode, setCurrentMode] = useState<string>('general_chat')
  const [isLoading, setIsLoading] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [workflowProgress, setWorkflowProgress] = useState<ConversationState['workflowProgress']>(null)
  const [error, setError] = useState<string | null>(null)
  const [nextActions, setNextActions] = useState<string[]>([])
  const [memoryContext, setMemoryContext] = useState<any>(null)
  
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Generate unique message ID
  const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    console.log('📨 WebSocket message:', message)

    const stageToAgentName: Record<string, string> = {
      conversation_processing: 'Conversational Orchestrator',
      project_intake: 'Project Intake Agent',
      solution_architecture: 'Solution Architecture Agent',
      scope_decomposition: 'Scope Decomposition Agent',
      effort_estimation: 'Effort Estimation Agent',
      proposal_generation: 'Proposal Generation Agent',
      success_assessment: 'Success Assessment Agent',
      proposal_review: 'Proposal Review Agent',
    }

    switch (message.type) {
      case 'connection_confirmed':
        setIsConnected(true)
        break

      case 'stage_update':
        console.log('🔄 Stage update:', message.stage, message.status)

        setWorkflowProgress(prev => {
          const base = prev || {}
          const agentNameFromEvent = message.data?.agent_name || message.data?.agentName
          const inferredAgentName = message.stage ? stageToAgentName[message.stage] : undefined

          const startedAt = base.startedAt ?? Date.now()

          const previousCompleted = base.stagesCompleted || 0
          const nextCompleted =
            message.status === 'completed' ? previousCompleted + 1 : previousCompleted

          const updated = {
            ...base,
            currentStage: message.stage,
            status: message.status || base.status,
            stagesCompleted: message.data?.stages_completed || nextCompleted,
            totalStages: 7,
            agentName: agentNameFromEvent || inferredAgentName || base.agentName,
            message: message.data?.message || base.message,
            startedAt,
          }
          console.log('Updated workflow progress:', updated)
          return updated
        })
        break

      case 'workflow_complete':
        console.log('✅ Workflow complete:', message.results)

        setWorkflowProgress(prev => ({
          ...(prev || {}),
          status: 'completed',
          stagesCompleted: message.results?.stages_completed || 7,
          totalStages: 7,
          currentStage: undefined,
          agentName: undefined,
          message: undefined,
        }))

        // Add completion message
        const completionMessage: Message = {
          id: generateMessageId(),
          type: 'system',
          content: `✅ Workflow completed successfully! Your proposal is ready to view.`,
          timestamp: new Date(),
          metadata: {
            status: 'complete',
            workflow_result: message.results
          }
        }
        setMessages(prev => [...prev, completionMessage])
        break

      case 'error':
        setError(`Workflow error${message.stage ? ` in ${message.stage}` : ''}: ${message.error}`)
        break
    }
  }, [])

  // Setup WebSocket connection
  const setupWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return // Already connected
    }

    try {
      wsRef.current = conversationalApi.createWebSocket(
        projectId,
        handleWebSocketMessage,
        (error) => {
          console.error('WebSocket error:', error)
          setIsConnected(false)
          // Disable auto-reconnect for now to prevent flooding
          // reconnectTimeoutRef.current = setTimeout(setupWebSocket, 3000)
        },
        (event) => {
          console.log('WebSocket closed:', event)
          setIsConnected(false)
          // Disable auto-reconnect for now to prevent flooding
          // if (event.code !== 1000) {
          //   reconnectTimeoutRef.current = setTimeout(setupWebSocket, 3000)
          // }
        }
      )
    } catch (error) {
      console.error('Failed to setup WebSocket:', error)
      setError('Failed to establish real-time connection')
    }
  }, [projectId, handleWebSocketMessage])

  // Initialize WebSocket on mount
  useEffect(() => {
    // Only setup WebSocket if we have a valid projectId
    if (projectId && projectId.trim() !== '') {
      console.log('🔌 Setting up WebSocket for project:', projectId)
      setupWebSocket()
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (wsRef.current) {
        console.log('🔌 Closing WebSocket')
        wsRef.current.close(1000, 'Component unmounting')
      }
    }
  }, [projectId, setupWebSocket])

  // Load existing conversation history
  useEffect(() => {
    // Clear state immediately when projectId changes to prevent showing old data
    console.log('📂 Project changed, clearing state and loading history for:', projectId)
    setMessages([])
    setWorkflowProgress(null)
    setMemoryContext(null)
    setCurrentMode('general_chat')
    setNextActions([])

    // Skip loading if projectId is empty
    if (!projectId || projectId.trim() === '') {
      console.log('📂 No project ID, skipping history load')
      return
    }

    const loadConversationHistory = async () => {
      try {
        console.log('📂 Loading conversation history for project:', projectId)
        const status = await conversationalApi.getProjectStatus(projectId)

        // Convert API conversation history to messages
        const historyMessages: Message[] = status.conversation_history.map((msg, index) => ({
          id: `history_${index}`,
          type: msg.type as 'user' | 'assistant' | 'system',
          content: msg.content,
          timestamp: new Date(msg.timestamp),
        }))

        console.log(`📂 Loaded ${historyMessages.length} messages from history`)
        setMessages(historyMessages)
        setMemoryContext(status.memory_context)

        if (status.workflow_status) {
          setWorkflowProgress({
            status: status.workflow_status.status,
            stagesCompleted: status.workflow_status.stages_completed,
            currentStage: status.workflow_status.current_stage
          })
        }

        // If project has a proposal, set mode to proposal_refinement
        if (status.memory_context?.has_proposal) {
          console.log('📂 Project has proposal, setting mode to proposal_refinement')
          setCurrentMode('proposal_refinement')
        }
      } catch (error) {
        // No existing conversation - this is fine for new projects
        console.log('📂 No existing conversation history for project:', projectId)
      }
    }

    loadConversationHistory()
  }, [projectId])

  // Send message to conversational orchestrator
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return

    setIsLoading(true)
    setError(null)
    setNextActions([])

    // Add user message immediately
    const userMessage: Message = {
      id: generateMessageId(),
      type: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, userMessage])

    try {
      // Get current conversation history for context
      const conversationHistory: ConversationMessage[] = messages.map(msg => ({
        type: msg.type,
        content: msg.content,
        timestamp: msg.timestamp.toISOString()
      }))

      // Send to conversational orchestrator
      const response: ConversationResponse = await conversationalApi.sendMessage(
        content.trim(),
        projectId,
        conversationHistory
      )

      // Add assistant response
      const assistantMessage: Message = {
        id: generateMessageId(),
        type: 'assistant',
        content: response.response,
        timestamp: new Date(),
        mode: response.mode,
        metadata: {
          workflow_result: response.workflow_result,
          status: 'complete'
        }
      }
      setMessages(prev => [...prev, assistantMessage])

      // Update conversation state
      setCurrentMode(response.mode || 'general_chat')
      setNextActions(response.next_actions || [])
      setMemoryContext(response.metadata?.memory_context || null)

      // Handle workflow results
      if (response.workflow_result?.success) {
        setWorkflowProgress({
          status: response.workflow_result.status,
          stagesCompleted: response.workflow_result.stages_completed,
          totalStages: 7 // Enhanced workflow has 7 stages
        })
      }

      // Return response mode so caller can trigger proposal refresh if needed
      return response

    } catch (error) {
      const errorMessage = error instanceof ConversationalApiError 
        ? error.message 
        : 'Failed to send message'
      
      setError(errorMessage)
      
      // Add error message to conversation
      const errorMsg: Message = {
        id: generateMessageId(),
        type: 'system',
        content: `❌ Error: ${errorMessage}`,
        timestamp: new Date(),
        metadata: { status: 'complete' }
      }
      setMessages(prev => [...prev, errorMsg])
      
      return undefined
    } finally {
      setIsLoading(false)
    }
  }, [projectId, messages, isLoading])

  // Upload file through conversational orchestrator
  const uploadFile = useCallback(async (files: File | File[]) => {
    const fileArray = Array.isArray(files) ? files : [files]
    const fileNames = fileArray.map(f => f.name).join(', ')
    console.log('🔥 useConversation uploadFile called with:', fileArray.length, 'file(s), projectId:', projectId)
    
    if (isLoading) {
      console.log('🔥 Upload blocked - already loading')
      return
    }

    setIsLoading(true)
    setError(null)
    setNextActions([])

    // Add file upload message
    const uploadMessage: Message = {
      id: generateMessageId(),
      type: 'user',
      content: fileArray.length === 1 
        ? `📎 Uploaded: ${fileArray[0].name}` 
        : `📎 Uploaded ${fileArray.length} files: ${fileNames}`,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, uploadMessage])

    try {
      console.log('🔥 About to call conversationalApi.uploadFile')
      const response = await conversationalApi.uploadFile(fileArray, projectId)
      console.log('🔥 Got response from conversationalApi.uploadFile:', response)

      // Add processing result
      const resultMessage: Message = {
        id: generateMessageId(),
        type: 'assistant',
        content: response.conversation_result.response,
        timestamp: new Date(),
        mode: response.conversation_result.mode,
        metadata: {
          workflow_result: response.conversation_result.workflow_result,
          status: 'complete'
        }
      }
      setMessages(prev => [...prev, resultMessage])

      // Update state from conversation result
      if (response.conversation_result) {
        setCurrentMode(response.conversation_result.mode || 'general_chat')
        setNextActions(response.conversation_result.next_actions || [])

        if (response.conversation_result.workflow_result?.success) {
          setWorkflowProgress({
            status: response.conversation_result.workflow_result.status,
            stagesCompleted: response.conversation_result.workflow_result.stages_completed,
            totalStages: 7
          })
        }
      }

    } catch (error) {
      const errorMessage = error instanceof ConversationalApiError 
        ? error.message 
        : 'Failed to upload file'
      
      setError(errorMessage)
      
      const errorMsg: Message = {
        id: generateMessageId(),
        type: 'system',
        content: `❌ Upload failed: ${errorMessage}`,
        timestamp: new Date(),
        metadata: { status: 'complete' }
      }
      setMessages(prev => [...prev, errorMsg])
    } finally {
      setIsLoading(false)
    }
  }, [projectId, isLoading])

  // Get proposal
  const getProposal = useCallback(async (): Promise<string | null> => {
    try {
      return await conversationalApi.getProposal(projectId)
    } catch (error) {
      console.error('Failed to get proposal:', error)
      setError('Failed to load proposal')
      return null
    }
  }, [projectId])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Reconnect WebSocket
  const reconnectWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close()
    }
    setupWebSocket()
  }, [setupWebSocket])

  // Clear conversation
  const clearConversation = useCallback(() => {
    setMessages([])
    setCurrentMode('general_chat')
    setWorkflowProgress(null)
    setNextActions([])
    setMemoryContext(null)
    setError(null)
  }, [])

  return {
    // State
    messages,
    currentMode,
    isLoading,
    isConnected,
    workflowProgress,
    error,
    nextActions,
    memoryContext,
    
    // Actions
    sendMessage,
    uploadFile,
    clearError,
    reconnectWebSocket,
    getProposal,
    clearConversation,
  }
}
